import { Button as AntButton, Modal, Select, Table, TableColumnsType } from 'antd';
import { FC, useEffect, useState } from 'react';
import Button from '../../../Button';
import config from '../../../../lib/config';
import Pagination from '../../../Pagination';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/rootReducer';
import { useTranslation } from 'react-i18next';
import useAxios from '../../../../hooks/useAxios';
import { IGetShuttleList } from '../../../../models/students.model';
import { createShuttleInfo, getShuttleList } from '../../../../api/students.api';
import Loader from '../../../Loader';
import ErrorResponse from '../../../ErrorResponse';
import CustomTimePicker from '../../../CustomTimePicker';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  campusCode?: string;
  studentCode?: string;
  doGetShuttleInfo: () => void;
}

const SelectShuttleModal: FC<IProps> = ({
  visible,
  setVisible,
  campusCode,
  studentCode,
  doGetShuttleInfo,
}) => {
  const { t } = useTranslation();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // SY0001 = 요일
  const SY0001 = commonCodes.find(item => item.commonCode === 'SY0001')?.detailCode;
  const { data: res, isLoading, error, execute } = useAxios<IGetShuttleList>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();
  const [weekdayDiv, setWeekdayDiv] = useState('MO');
  const [lineTime, setLineTime] = useState('');
  const [stationName, setStationName] = useState('');
  const [pageNo, setPageNo] = useState(1);
  const [params, setParams] = useState<any>({});

  useEffect(() => {
    doGetShuttleList();
  }, [pageNo]);

  useEffect(() => {
    if (submitRes) {
      doGetShuttleInfo();
      setVisible(false);
    }
  }, [submitRes]);

  const doGetShuttleList = (_params?: any) => {
    if (campusCode && weekdayDiv) {
      execute(getShuttleList(
        campusCode,
        weekdayDiv,
        _params ||
        {
          ...params,
          pageNo,
          perPage: config.miniPageSize,
        },
      ));
    }
  };

  const onSearch = () => {
    const params: any = {
      pageNo: 1,
      perPage: config.miniPageSize,
    };    
    if (lineTime) params.lineTime = lineTime;
    if (stationName) params.stationName = stationName;
    setParams(params);
    doGetShuttleList(params);
  };

  const onSubmit = (runningStationCode: number, rideGbn: string) => {
    if (!campusCode || !studentCode) return;
    submitExecute(createShuttleInfo(campusCode, studentCode, {
      runningStationCode,
      rideGbn
    }));
  }

  const columns: TableColumnsType<any> = [
    {
      title: t('student.student.column69'),
      dataIndex: 'lineTime',
      key: 'lineTime',
      align: 'center',
      sorter: (a, b) => a.lineTime?.localeCompare(b.lineTime),

    },
    {
      title: t('student.student.column19'),
      dataIndex: 'busName',
      key: 'busName',
      align: 'center',
      sorter: (a, b) => a.busName?.localeCompare(b.busName),
    },
    {
      title: t('student.student.column20'),
      dataIndex: 'stationName',
      key: 'stationName',
      align: 'center',
      sorter: (a, b) => a.stationName?.localeCompare(b.stationName),
      render: value => {
        return (<div className='text-left ant-table-cell-ellipsis'>{value}</div>);
      }
    },
    {
      title: '',
      dataIndex: 'runningStationCode',
      key: 'runningStationCode',
      align: 'center',
      render: value => (
        <div className="row comm-btn__area justify-content-center">
          <Button
            className="btn-basic small mint"
            onClick={() => onSubmit(value, 'UP')}
            text={t('student.student.column67')}
          />
          <Button
            className="btn-basic small mint"
            onClick={() => onSubmit(value, 'DN')}
            text={t('student.student.column68')}
          />
        </div>
      )
    },
  ];

  return (
    <>
      {isLoading || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />

      <Modal
        title={t('student.student.title4')}
        visible={visible}
        onCancel={() => setVisible(false)}
        width={750}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={() => setVisible(false)}
            >
              {t('common.button.close')}
            </AntButton>
          </div>
        ]}
      >

        <div className="common-search">
          <div className="search-area">
            <ul className="search-bx" style={{justifyContent:'right'}}>
              <li>
                <label>{t('student.student.84')}</label>
                <Select
                  className="w-150p"
                  placeholder={t('student.student.column18')}
                  value={weekdayDiv}
                  onChange={value => setWeekdayDiv(value)}
                >
                  {
                    SY0001?.map((item: any) => {
                      return (
                        <Select.Option
                          key={item.detailCode}
                          value={item.detailCode}
                        >
                          {item.korName}
                        </Select.Option>
                      )
                    })
                  }
                </Select>
              </li>
              <li>
                <label>{t('student.student.85')}</label>
                <div className='time-picker__area'>
                  <CustomTimePicker
                    value={lineTime}
                    onSelect={value => {
                      if (value) setLineTime(value.format('HH:mm'));
                      else setLineTime('');
                    }}
                  />
                </div>
              </li>
              <li>
                <label>{t('student.student.86')}</label>
                <div className="w-150p">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('student.student.87')}
                    value={stationName}
                    onChange={e => setStationName(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === 'Enter') onSearch();
                    }}
                  />
                </div>
                <div className="comm-btn__area">
                  <Button
                    className="btn-basic small black"
                    onClick={onSearch}
                    text={t('common.button.search')}
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
        <Table
          className='comm-table__area'
          columns={columns}
          dataSource={res?.data}
          pagination={{
            current: pageNo,
            pageSize: config.miniPageSize,
          }}
          rowKey="runningStationCode"
        />
        <div className="comm-bx__space">
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={pageNo}
              setPage={setPageNo}
              mini
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

export default SelectShuttleModal;
